import { useEffect, useState } from 'react';
import type { TaskWithRelations, ProviderResponse, AppointmentTypeResponse } from '@vagusx/clients';
import { useProviders, useProviderLocations } from '@/hooks/use-providers';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import {
  useSimulateAppointmentTypeForTask,
  useScopedTypeRecommendation,
} from '@/hooks/use-appointment-type-matching';
import { normalizeRecommendation } from '@/components/custom/appointment-type-recommendation';
import { canResolveReferralDocument } from '@/hooks/use-referrals';

export interface ProviderLocationOption {
  location: { id: string; name: string };
  isPrimary: boolean;
}

export interface SchedulingScope {
  providerId: string;
  setProviderId: (id: string) => void;
  appointmentTypeId: string;
  setAppointmentTypeId: (id: string) => void;
  locationId: string;
  setLocationId: (id: string) => void;
  providers: ProviderResponse[];
  appointmentTypes: AppointmentTypeResponse[];
  locationOptions: ProviderLocationOption[];
  providerLocationsLoading: boolean;
  selectedProvider: ProviderResponse | undefined;
  selectedType: AppointmentTypeResponse | undefined;
  /** True once provider + appointment type + location are all chosen (required to offer slots). */
  ready: boolean;
}

/**
 * Which of a provider's locations to default to.
 *
 * `rankedLocationId` is the location the provider-selection algorithm scored
 * this provider AT — eligibility is a (provider, location, type) triple, so the
 * matched type may only be offered at one of their sites. Preferring it keeps
 * the seeded scope internally consistent; the primary location is the fallback
 * for a manual pick, where there is no ranked answer to honour.
 *
 * Pure and exported for test — a wrong default here silently warms the wrong
 * site and the agent finds no times.
 */
export function pickPreferredLocation(
  options: ProviderLocationOption[],
  rankedLocationId: string | null
): string | null {
  if (options.length === 0) return null;
  const ranked = options.find((l) => l.location.id === rankedLocationId);
  const preferred = ranked ?? options.find((l) => l.isPrimary) ?? options[0]!;
  return preferred.location.id;
}

/**
 * The provider/appointment-type/location scope every scheduling entry point
 * needs (the offerable-slots engine is scoped: all three are required). Owns the
 * shared state + the three effects — recommendation seeding from a task, reset-
 * location-on-provider-change, and auto-select-the-primary-location — in ONE
 * place so the voice call drawer, the text drawer, and the /sms/test tool can't
 * drift. Pass the referral `task` to seed the appointment type from the matcher.
 */
export function useSchedulingScope(task?: TaskWithRelations): SchedulingScope {
  const [providerId, setProviderId] = useState('');
  const [appointmentTypeId, setAppointmentTypeId] = useState('');
  const [locationId, setLocationId] = useState('');

  const { data: providersData } = useProviders();
  const providers = providersData?.items ?? [];
  const { data: appointmentTypesData } = useAppointmentTypes();
  const appointmentTypes = appointmentTypesData ?? [];
  const { data: providerLocations, isLoading: providerLocationsLoading } = useProviderLocations(
    providerId || undefined
  );
  const locationOptions = providerLocations ?? [];

  // Seed the appointment type from the referral matcher's recommendation, only
  // until the operator picks. Disabled when there's no resolvable task.
  const couldResolveDocument = task ? canResolveReferralDocument(task) : false;
  const { data: simulation } = useSimulateAppointmentTypeForTask(
    task?.id ?? '',
    Boolean(task) && couldResolveDocument
  );
  const recommendedTypeId = simulation
    ? normalizeRecommendation(simulation.recommendation).effectiveResult.appointmentTypeId
    : null;
  useEffect(() => {
    if (recommendedTypeId) setAppointmentTypeId((cur) => cur || recommendedTypeId);
  }, [recommendedTypeId]);

  // Org-wide, two types can score identically and the matcher returns nothing
  // rather than guessing — which left the field blank even though a provider
  // had already been chosen. But eligibility is a (provider, location, type)
  // triple, so the provider is itself a disambiguator: re-matching against just
  // that pair often leaves exactly one type enabled, and then there is nothing
  // to guess about.
  //
  // A FALLBACK, not a replacement: the org-wide answer is already loaded and
  // needs no provider, so it still seeds immediately when it can decide. This
  // only fills the gap it leaves. Both use `cur ||`, so whichever resolves
  // first wins and neither overwrites the operator.
  //
  // Still abstains when the pair is genuinely ambiguous — two types the same
  // clinician is equally enabled for is a real question for a human, and the
  // chosen id is written to the EHR as the appointment's type.
  const { data: scopedRecommendation } = useScopedTypeRecommendation(
    simulation?.signals,
    providerId,
    locationId
  );
  const scopedTypeId = scopedRecommendation
    ? normalizeRecommendation(scopedRecommendation).effectiveResult.appointmentTypeId
    : null;
  useEffect(() => {
    if (scopedTypeId) setAppointmentTypeId((cur) => cur || scopedTypeId);
  }, [scopedTypeId]);

  // Seed the PROVIDER from the same simulation — rank 1 of the ranked pool
  // (ADR-141), which is the provider selection already decided this referral
  // should be offered first. Same "only until the operator picks" contract as
  // the type above: `cur ||` never overwrites a choice.
  //
  // Without this the operator was left to pick blind from the full roster while
  // the server had already computed an ordered answer.
  const topRanked = simulation?.recommendation.explanation.providers[0];
  const rankedProviderId = topRanked?.providerId ?? null;
  useEffect(() => {
    if (rankedProviderId) setProviderId((cur) => cur || rankedProviderId);
  }, [rankedProviderId]);

  // A stale location from a different provider would warm/book the wrong place.
  useEffect(() => {
    setLocationId('');
  }, [providerId]);
  // Auto-select once the provider's locations load: the only one, or the primary.
  //
  // When the selected provider IS the ranked suggestion, prefer the location it
  // was ranked AT — that's the (provider, location) pair whose eligibility the
  // matcher actually scored, so defaulting to the primary instead could warm a
  // site where this provider doesn't offer the matched type.
  const rankedLocationId =
    topRanked && topRanked.providerId === providerId ? topRanked.locationId : null;
  useEffect(() => {
    if (locationOptions.length === 0) return;
    if (locationOptions.length === 1) {
      setLocationId(locationOptions[0]!.location.id);
      return;
    }
    const preferred = pickPreferredLocation(locationOptions, rankedLocationId);
    if (preferred) setLocationId((cur) => cur || preferred);
  }, [locationOptions, rankedLocationId]);

  return {
    providerId,
    setProviderId,
    appointmentTypeId,
    setAppointmentTypeId,
    locationId,
    setLocationId,
    providers,
    appointmentTypes,
    locationOptions,
    providerLocationsLoading,
    selectedProvider: providers.find((p) => p.id === providerId),
    selectedType: appointmentTypes.find((t) => t.id === appointmentTypeId),
    ready: !!providerId && !!appointmentTypeId && !!locationId,
  };
}
